import { type EpisodEvent, init, sendEvent, set } from "@ingka-group/episod-client-js";
import { anchorFromSearch, isAnchorPage } from "../utils/anchor";

/** High-level categories for grouping events in Episod / Looker. */
export type Category = "Views" | "Filters" | "Drilldown" | "Export" | "Navigation" | "Other";

export interface AnalyticsEvent {
    category: Category;
    /** Becomes the Episod `event_name`. */
    action: string;
    label: string;
    /** Optional structured payload, stored under `event_details.custom`. */
    custom?: Record<string, unknown>;
}

/** The Episod dataset name for MCC events. */
const EVENT_ORIGIN = "A2P-MCC";

// Not secret — visible in the network tab on any IKEA app using Episod.
// Reused from the shared Livlig client; MCC events are kept separate via `event_origin`.
const EPISOD_ENV = {
    dev: {
        url: "https://web-api.cte.ikeadt.com/episod/collect",
        xClientId: "a0bb26de-64e2-4000-8b31-076cdb84c29d",
    },
    prod: {
        url: "https://web-api.ikea.com/episod/collect",
        xClientId: "1ea83eeb-f16f-43d0-a11d-2487f62fb456",
    },
} as const;

function isProdHost(): boolean {
    const host = window.location.hostname;
    return !host.includes(".dev") && host !== "localhost";
}

function isLocalhost(): boolean {
    return typeof window !== "undefined" && window.location.hostname === "localhost";
}

type UrlParts = Pick<Location, "pathname" | "search">;

/**
 * Add the anchor the user is viewing (e.g. PAX, BESTÅ) as `custom.anchor`.
 * Read from the live URL at send time, so it never lags behind React effects.
 * Pages that aren't about an anchor (e.g. Features) get no anchor, so they
 * don't inflate the default (PAX) in per-anchor splits.
 */
export function withAnchor(event: EpisodEvent, url: UrlParts = window.location): EpisodEvent {
    const details = event.event_details ?? {};
    // Intentionally overrides any caller-supplied `anchor` (e.g. the raw URL
    // value in page views) so it is always normalised, and absent off anchor
    // pages.
    const custom: Record<string, unknown> = { ...details.custom };
    delete custom.anchor;
    if (isAnchorPage(url.pathname)) custom.anchor = anchorFromSearch(url.search);
    return {
        ...event,
        event_details: {
            ...details,
            custom,
        },
    };
}

/** On localhost, log instead of posting to the collect endpoint. */
function dispatchEvent(rawEvent: EpisodEvent): void {
    const event = withAnchor(rawEvent);
    if (isLocalhost()) {
        console.debug("[episod] event", event);
        return;
    }
    sendEvent(event);
}

export interface UserCommons {
    appName: string;
    language: string;
    sub?: string | null;
    country?: string | null;
    department?: string | null;
    jobTitle?: string | null;
    locationId?: string | null;
    officeLocation?: string | null;
}

/** Initialise the Episod client once for the current environment. */
export function initEpisod(): void {
    const env = isProdHost() ? EPISOD_ENV.prod : EPISOD_ENV.dev;
    init({
        cookieDomain: window.location.hostname,
        url: env.url,
        xClientId: env.xClientId,
        commons: { event_origin: EVENT_ORIGIN },
    });
}

/** Attach user role/location context to every subsequent event. */
export function setCommons(user: UserCommons): void {
    set({
        pageview_id: user.appName,
        logged_in: true,
        event_details: {
            location_language: user.language,
            custom: {
                subject: user.sub ?? undefined,
                user_country: user.country ?? undefined,
                user_department: user.department ?? undefined,
                user_job_title: user.jobTitle ?? undefined,
                user_location_id: user.locationId ?? undefined,
                user_office_location: user.officeLocation ?? undefined,
            },
        },
    });
}

export function sendPageView(
    path: string,
    page: string,
    custom?: Record<string, unknown>,
): void {
    const event: EpisodEvent = {
        event_name: "Page viewed",
        event_location: path,
        event_details: {
            category: "Views",
            label: `${page} viewed`,
            location_title: typeof document !== "undefined" ? document.title : undefined,
            custom: { page, ...custom },
        },
    };
    dispatchEvent(event);
}

export function sendAnalyticsEvent(event: AnalyticsEvent): void {
    dispatchEvent({
        event_name: event.action,
        event_details: {
            category: event.category,
            label: event.label,
            custom: event.custom,
        },
    });
}

/**
 * Hook exposing the Episod helpers. The returned functions are module-level
 * and therefore stable across renders, so they're safe to use in `useCallback`
 * / `useEffect` dependency arrays.
 */
export function useEpisod() {
    return { initEpisod, setCommons, sendPageView, sendAnalyticsEvent };
}
